<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  src: { type: String, default: '' },
  fileType: { type: String, default: 'image' },
  fileName: { type: String, default: '' },
});

const emit = defineEmits(['close']);

const imgRef = ref(null);
const isZoomed = ref(false);

function close() {
  emit('close');
}

function toggleZoom() {
  isZoomed.value = !isZoomed.value;
}

function download() {
  const a = document.createElement('a');
  a.href = props.src;
  a.download = props.fileName || 'download';
  a.target = '_blank';
  a.rel = 'noopener noreferrer';
  a.click();
}

function handleKeyDown(e) {
  if (e.key === 'Escape') close();
}

onMounted(() => {
  document.addEventListener('keydown', handleKeyDown);
  document.body.style.overflow = 'hidden';
});

onBeforeUnmount(() => {
  document.removeEventListener('keydown', handleKeyDown);
  document.body.style.overflow = '';
});
</script>

<template>
  <Teleport to="body">
    <div
      class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm"
      @click.self="close"
    >
      <!-- Toolbar -->
      <div class="absolute right-4 top-4 flex items-center gap-2">
        <button
          class="flex size-9 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
          title="Download"
          @click="download"
        >
          <Icon icon="i-lucide-download" class="size-5" />
        </button>
        <a
          :href="src"
          target="_blank"
          rel="noopener noreferrer"
          class="flex size-9 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
          title="Abrir em nova aba"
        >
          <Icon icon="i-lucide-external-link" class="size-5" />
        </a>
        <button
          class="flex size-9 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
          title="Fechar"
          @click="close"
        >
          <Icon icon="i-lucide-x" class="size-5" />
        </button>
      </div>

      <!-- Image viewer -->
      <div
        v-if="fileType === 'image'"
        class="flex max-h-[90vh] max-w-[90vw] items-center justify-center"
        :class="isZoomed ? 'overflow-auto' : 'overflow-hidden'"
      >
        <img
          ref="imgRef"
          :src="src"
          :alt="fileName"
          class="rounded-lg shadow-2xl transition-transform duration-200 select-none"
          :class="
            isZoomed
              ? 'max-h-none max-w-none cursor-zoom-out'
              : 'max-h-[90vh] max-w-[90vw] object-contain cursor-zoom-in'
          "
          @click="toggleZoom"
        />
      </div>

      <!-- Video viewer -->
      <div
        v-else-if="fileType === 'video'"
        class="flex max-h-[90vh] max-w-[90vw] items-center justify-center"
      >
        <video
          :src="src"
          class="max-h-[90vh] max-w-[90vw] rounded-lg shadow-2xl"
          controls
          autoplay
        />
      </div>

      <!-- Audio player -->
      <div
        v-else-if="fileType === 'audio'"
        class="flex items-center justify-center rounded-xl bg-n-solid-2 p-6 shadow-2xl"
      >
        <audio :src="src" controls autoplay class="w-80" />
      </div>

      <!-- Filename caption -->
      <div
        v-if="fileName"
        class="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-black/50 px-4 py-1.5 text-sm text-white"
      >
        {{ fileName }}
      </div>
    </div>
  </Teleport>
</template>
